import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VaultKeTheme } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '600px',
      width: '100%',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    logo: {
      width: '80px',
      height: '80px',
      backgroundColor: VaultKeTheme.colors.primary,
      borderRadius: '50%',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
    },
    logoText: {
      fontSize: VaultKeTheme.typography.h1.fontSize,
      fontWeight: VaultKeTheme.typography.h1.fontWeight,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    card: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.xl,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.lg,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: VaultKeTheme.spacing.lg,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    formGroup: {
      marginBottom: VaultKeTheme.spacing.lg,
    },
    label: {
      display: 'block',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    input: {
      width: '100%',
      padding: VaultKeTheme.spacing.md,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.text,
      fontSize: VaultKeTheme.typography.body1.fontSize,
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    inputError: {
      borderColor: VaultKeTheme.colors.error,
    },
    passwordContainer: {
      position: 'relative',
    },
    passwordToggle: {
      position: 'absolute',
      right: VaultKeTheme.spacing.md,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.textSecondary,
      cursor: 'pointer',
      fontSize: '18px',
    },
    genderContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: VaultKeTheme.spacing.sm,
    },
    genderOption: {
      padding: VaultKeTheme.spacing.md,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center',
    },
    genderOptionSelected: {
      borderColor: VaultKeTheme.colors.primary,
      backgroundColor: VaultKeTheme.colors.primary + '20',
    },
    genderIcon: {
      fontSize: '24px',
      marginBottom: VaultKeTheme.spacing.xs,
    },
    genderText: {
      fontSize: VaultKeTheme.typography.body2.fontSize,
      color: VaultKeTheme.colors.text,
    },
    termsContainer: {
      marginBottom: VaultKeTheme.spacing.xl,
    },
    termsCheckbox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: VaultKeTheme.spacing.sm,
    },
    checkbox: {
      width: '20px',
      height: '20px',
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.sm,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
    },
    checkboxChecked: {
      backgroundColor: VaultKeTheme.colors.primary,
      borderColor: VaultKeTheme.colors.primary,
    },
    termsText: {
      fontSize: VaultKeTheme.typography.body2.fontSize,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.4',
    },
    termsLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontWeight: '600',
    },
    errorText: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      color: VaultKeTheme.colors.error,
      marginTop: VaultKeTheme.spacing.xs,
    },
    button: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: `${VaultKeTheme.spacing.xl} 0`,
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: VaultKeTheme.colors.border,
    },
    dividerText: {
      padding: `0 ${VaultKeTheme.spacing.md}`,
      color: VaultKeTheme.colors.textSecondary,
      fontSize: VaultKeTheme.typography.body2.fontSize,
    },
    signInContainer: {
      textAlign: 'center',
    },
    signInText: {
      color: VaultKeTheme.colors.textSecondary,
      fontSize: VaultKeTheme.typography.body2.fontSize,
    },
    signInLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontWeight: '600',
    },
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!acceptedTerms) newErrors.terms = 'Please accept the terms and conditions';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          password: formData.password,
          gender: formData.gender || undefined,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Store auth token and user data
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('userData', JSON.stringify(data.data.user));

        // Navigate to email verification page
        navigate('/email-verification');
      } else {
        setErrors({ general: data.error || 'Registration failed' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Male', icon: '♂️' },
    { value: 'female', label: 'Female', icon: '♀️' },
    { value: 'other', label: 'Other', icon: '⚧️' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say', icon: '🤷‍♂️' },
  ];

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>VK</div>
          <h1 style={styles.logoText}>VaultKe</h1>
        </div>

        <div style={styles.card}>
          <div style={styles.header}>
            <h2 style={styles.title}>Create Account</h2>
            <p style={styles.subtitle}>Join thousands of users managing their finances</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="First name"
                  style={{
                    ...styles.input,
                    ...(errors.firstName ? styles.inputError : {}),
                  }}
                />
                {errors.firstName && <div style={styles.errorText}>{errors.firstName}</div>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Last name"
                  style={{
                    ...styles.input,
                    ...(errors.lastName ? styles.inputError : {}),
                  }}
                />
                {errors.lastName && <div style={styles.errorText}>{errors.lastName}</div>}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Email address"
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {}),
                }}
              />
              {errors.email && <div style={styles.errorText}>{errors.email}</div>}
            </div>

            {errors.general && (
              <div style={{
                ...styles.errorText,
                textAlign: 'center',
                marginBottom: VaultKeTheme.spacing.lg,
                padding: VaultKeTheme.spacing.md,
                backgroundColor: VaultKeTheme.colors.error + '20',
                borderRadius: VaultKeTheme.borderRadius.md,
                border: `1px solid ${VaultKeTheme.colors.error}`,
              }}>
                {errors.general}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Phone number"
                style={{
                  ...styles.input,
                  ...(errors.phone ? styles.inputError : {}),
                }}
              />
              {errors.phone && <div style={styles.errorText}>{errors.phone}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Gender (Optional)</label>
              <div style={styles.genderContainer}>
                {genderOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleInputChange('gender', option.value)}
                    style={{
                      ...styles.genderOption,
                      ...(formData.gender === option.value ? styles.genderOptionSelected : {}),
                    }}
                  >
                    <div style={styles.genderIcon}>{option.icon}</div>
                    <div style={styles.genderText}>{option.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Password"
                  style={{
                    ...styles.input,
                    ...(errors.password ? styles.inputError : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <div style={styles.errorText}>{errors.password}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm password"
                  style={{
                    ...styles.input,
                    ...(errors.confirmPassword ? styles.inputError : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.passwordToggle}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.confirmPassword && <div style={styles.errorText}>{errors.confirmPassword}</div>}
            </div>

            <div style={styles.termsContainer}>
              <div style={styles.termsCheckbox}>
                <div
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  style={{
                    ...styles.checkbox,
                    ...(acceptedTerms ? styles.checkboxChecked : {}),
                  }}
                >
                  {acceptedTerms && '✓'}
                </div>
                <div style={styles.termsText}>
                  I agree to the{' '}
                  <a href="/terms-of-service" style={styles.termsLink}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy-policy" style={styles.termsLink}>Privacy Policy</a>
                </div>
              </div>
              {errors.terms && <div style={styles.errorText}>{errors.terms}</div>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...styles.button,
                ...(isLoading ? styles.buttonDisabled : {}),
              }}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div style={styles.divider}>
            <div style={styles.dividerLine}></div>
            <span style={styles.dividerText}>OR</span>
            <div style={styles.dividerLine}></div>
          </div>

          <div style={styles.signInContainer}>
            <span style={styles.signInText}>Already have an account? </span>
            <a href="/login" style={styles.signInLink}>Sign In</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;